import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Icon, SelectableCard, Skeleton, isIconName, type IconName } from '@tb/ui';
import type { ChangeTemplateRequest } from '@tb/shared';
import { api } from '../api/endpoints';
import { CustomCodeCard } from '../components/CustomCodeCard';
import { Screen } from '../components/Layout';
import { useT } from '../i18n/LocaleProvider';
import { useManifestText } from '../i18n/useManifestText';

type Choice = { kind: 'TEMPLATE'; code: string } | { kind: 'CUSTOM' };

export function ChangeTemplateScreen() {
  const { siteId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [choice, setChoice] = useState<Choice | null>(null);
  const t = useT();
  const m = useManifestText();

  const siteQuery = useQuery({ queryKey: ['site', siteId], queryFn: () => api.sites.get(siteId) });
  const templatesQuery = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.templates.list(),
    staleTime: 5 * 60_000,
  });

  const changeMutation = useMutation({
    mutationFn: (body: ChangeTemplateRequest) => api.sites.changeTemplate(siteId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['site', siteId] });
      await queryClient.invalidateQueries({ queryKey: ['version-source', siteId] });
      await queryClient.invalidateQueries({ queryKey: ['sites'] });
      void navigate(`/sites/${siteId}/edit`, { replace: true });
    },
  });

  const site = siteQuery.data;
  const current: Choice | null = site
    ? site.kind === 'TEMPLATE'
      ? { kind: 'TEMPLATE', code: site.templateCode ?? '' }
      : { kind: 'CUSTOM' }
    : null;

  const isCurrent = (option: Choice): boolean =>
    current !== null &&
    current.kind === option.kind &&
    (option.kind === 'CUSTOM' || current.kind !== 'TEMPLATE' || current.code === option.code);

  const warning =
    choice === null
      ? null
      : choice.kind === 'CUSTOM'
        ? site?.kind === 'TEMPLATE'
          ? t('template.warn.toCustom')
          : null
        : t('template.warn.toTemplate');

  return (
    <Screen title={t('template.title')} back={`/sites/${siteId}`}>
      <div className="tb-screen">
        <span className="tb-subtitle">{t('template.subtitle')}</span>

        {changeMutation.isError ? (
          <Alert tone="danger" title={t('template.failed')}>
            {(changeMutation.error as Error).message}
          </Alert>
        ) : null}

        <section className="tb-stack">
          <span className="tb-label">{t('new.templates')}</span>
          {templatesQuery.isPending ? (
            <>
              <Skeleton height={78} />
              <Skeleton height={78} />
            </>
          ) : templatesQuery.isError ? (
            <Alert tone="danger">{t('new.templatesFailed')}</Alert>
          ) : (
            <div className="template-grid">
              {templatesQuery.data.map((template) => {
                const option: Choice = { kind: 'TEMPLATE', code: template.code };
                return (
                  <SelectableCard
                    key={template.code}
                    icon={toIconName(template.icon)}
                    title={m(template.name)}
                    description={
                      isCurrent(option) ? t('template.current') : m(template.description)
                    }
                    selected={choice?.kind === 'TEMPLATE' && choice.code === template.code}
                    disabled={isCurrent(option)}
                    onClick={() => setChoice(option)}
                  />
                );
              })}
            </div>
          )}
        </section>

        <section className="tb-stack">
          <span className="tb-label">{t('new.custom.section')}</span>
          <CustomCodeCard
            selected={choice?.kind === 'CUSTOM'}
            disabled={isCurrent({ kind: 'CUSTOM' })}
            description={
              isCurrent({ kind: 'CUSTOM' }) ? t('template.current') : t('template.toCustom')
            }
            onSelect={() => setChoice({ kind: 'CUSTOM' })}
          />
        </section>

        {warning ? (
          <Alert tone="warning" icon="warning" title={t('template.warn.title')}>
            {warning}
          </Alert>
        ) : null}

        <div className="tb-actionbar">
          <Button
            block
            icon="layers"
            disabled={!choice}
            loading={changeMutation.isPending}
            onClick={() => {
              if (!choice) return;
              changeMutation.mutate(
                choice.kind === 'TEMPLATE'
                  ? { kind: 'TEMPLATE', templateCode: choice.code }
                  : { kind: 'CUSTOM' },
              );
            }}
          >
            {t('template.apply')}
          </Button>
        </div>

        <div className="tb-row" style={{ justifyContent: 'center' }}>
          <Icon name="history" size={14} />
          <span className="tb-hint">{t('template.historyHint')}</span>
        </div>
      </div>
    </Screen>
  );
}

function toIconName(value: string): IconName {
  return isIconName(value) ? value : 'template';
}
